import { TenantConfig } from '@/types/tenant';

export interface ThemeConfig {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
  };
  typography: {
    fontFamily: string;
    headingWeight: string;
    bodyWeight: string;
  };
  spacing: {
    section: string;
    container: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
  };
  shadows: {
    small: string;
    medium: string;
    large: string;
  };
  animations: {
    duration: string;
    easing: string;
  };
}

export const THEME_PRESETS: Record<string, ThemeConfig> = {
  elegant: {
    id: 'elegant',
    name: 'Elegante',
    description: 'Diseño sofisticado y profesional',
    colors: {
      primary: '#1e40af',
      secondary: '#3b82f6',
      accent: '#f59e0b',
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#1f2937'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingWeight: '700',
      bodyWeight: '400'
    },
    spacing: {
      section: '4rem',
      container: '1.5rem'
    },
    borderRadius: {
      small: '0.375rem',
      medium: '0.75rem',
      large: '1rem'
    },
    shadows: {
      small: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      medium: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      large: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
    },
    animations: {
      duration: '300ms',
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
    }
  },
  coastal: {
    id: 'coastal',
    name: 'Costero',
    description: 'Inspirado en la costa y el mar',
    colors: {
      primary: '#059669',
      secondary: '#10b981',
      accent: '#f97316',
      background: '#ffffff',
      surface: '#f0fdfa',
      text: '#064e3b'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingWeight: '600',
      bodyWeight: '400'
    },
    spacing: {
      section: '5rem',
      container: '1.5rem'
    },
    borderRadius: {
      small: '0.5rem',
      medium: '1rem',
      large: '1.5rem'
    },
    shadows: {
      small: '0 1px 3px 0 rgb(6 78 59 / 0.1)',
      medium: '0 4px 6px -1px rgb(6 78 59 / 0.1)',
      large: '0 20px 25px -5px rgb(6 78 59 / 0.1)'
    },
    animations: {
      duration: '400ms',
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    }
  },
  modern: {
    id: 'modern',
    name: 'Moderno',
    description: 'Diseño contemporáneo y minimalista',
    colors: {
      primary: '#7c3aed',
      secondary: '#a855f7',
      accent: '#06b6d4',
      background: '#ffffff',
      surface: '#fafafa',
      text: '#18181b'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingWeight: '800',
      bodyWeight: '400'
    },
    spacing: {
      section: '3rem',
      container: '1rem'
    },
    borderRadius: {
      small: '0.25rem',
      medium: '0.5rem',
      large: '0.75rem'
    },
    shadows: {
      small: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      medium: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      large: '0 25px 50px -12px rgb(0 0 0 / 0.25)'
    },
    animations: {
      duration: '200ms',
      easing: 'cubic-bezier(0.4, 0, 1, 1)'
    }
  },
  classic: {
    id: 'classic',
    name: 'Clásico',
    description: 'Estilo tradicional y atemporal',
    colors: {
      primary: '#92400e',
      secondary: '#d97706',
      accent: '#dc2626',
      background: '#fffbeb',
      surface: '#fef3c7',
      text: '#451a03'
    },
    typography: {
      fontFamily: 'Georgia, serif',
      headingWeight: '700',
      bodyWeight: '400'
    },
    spacing: {
      section: '4rem',
      container: '2rem'
    },
    borderRadius: {
      small: '0.125rem',
      medium: '0.25rem',
      large: '0.5rem'
    },
    shadows: {
      small: '0 1px 3px 0 rgb(69 26 3 / 0.1)',
      medium: '0 4px 6px -1px rgb(69 26 3 / 0.1)',
      large: '0 20px 25px -5px rgb(69 26 3 / 0.1)'
    },
    animations: {
      duration: '500ms',
      easing: 'ease-in-out'
    }
  },
  eco: {
    id: 'eco',
    name: 'Ecológico',
    description: 'Inspirado en la naturaleza',
    colors: {
      primary: '#16a34a',
      secondary: '#22c55e',
      accent: '#eab308',
      background: '#f7fee7',
      surface: '#ecfccb',
      text: '#14532d'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingWeight: '600',
      bodyWeight: '400'
    },
    spacing: {
      section: '4rem',
      container: '1.5rem'
    },
    borderRadius: {
      small: '0.75rem',
      medium: '1rem',
      large: '1.5rem'
    },
    shadows: {
      small: '0 1px 3px 0 rgb(21 83 45 / 0.1)',
      medium: '0 4px 6px -1px rgb(21 83 45 / 0.1)',
      large: '0 20px 25px -5px rgb(21 83 45 / 0.1)'
    },
    animations: {
      duration: '350ms',
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
    }
  },
  luxury: {
    id: 'luxury',
    name: 'Lujo',
    description: 'Elegancia premium y exclusividad',
    colors: {
      primary: '#1f2937',
      secondary: '#374151',
      accent: '#fbbf24',
      background: '#111827',
      surface: '#1f2937',
      text: '#f9fafb'
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingWeight: '300',
      bodyWeight: '300'
    },
    spacing: {
      section: '6rem',
      container: '2rem'
    },
    borderRadius: {
      small: '0.125rem',
      medium: '0.25rem',
      large: '0.375rem'
    },
    shadows: {
      small: '0 1px 3px 0 rgb(0 0 0 / 0.3)',
      medium: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
      large: '0 20px 25px -5px rgb(0 0 0 / 0.4)'
    },
    animations: {
      duration: '600ms',
      easing: 'cubic-bezier(0.23, 1, 0.32, 1)'
    }
  }
};

export function getThemeConfig(style: string): ThemeConfig {
  return THEME_PRESETS[style] || THEME_PRESETS.elegant;
}

export function applyTheme(config: TenantConfig) {
  if (typeof document === 'undefined') return;

  const theme = getThemeConfig(config.theme.style || 'elegant');
  const root = document.documentElement;

  // Apply tenant colors
  root.style.setProperty('--primary-color', config.primaryColor);
  root.style.setProperty('--secondary-color', config.secondaryColor);
  root.style.setProperty('--accent-color', config.accentColor);

  // Apply theme variables
  root.style.setProperty('--theme-background', theme.colors.background);
  root.style.setProperty('--theme-surface', theme.colors.surface);
  root.style.setProperty('--theme-text', theme.colors.text);
  
  root.style.setProperty('--theme-font-family', theme.typography.fontFamily);
  root.style.setProperty('--theme-heading-weight', theme.typography.headingWeight);
  root.style.setProperty('--theme-body-weight', theme.typography.bodyWeight);
  
  root.style.setProperty('--theme-section-spacing', theme.spacing.section);
  root.style.setProperty('--theme-container-padding', theme.spacing.container);
  
  root.style.setProperty('--theme-radius-sm', theme.borderRadius.small);
  root.style.setProperty('--theme-radius-md', theme.borderRadius.medium);
  root.style.setProperty('--theme-radius-lg', theme.borderRadius.large);
  
  root.style.setProperty('--theme-shadow-sm', theme.shadows.small);
  root.style.setProperty('--theme-shadow-md', theme.shadows.medium);
  root.style.setProperty('--theme-shadow-lg', theme.shadows.large);
  
  root.style.setProperty('--theme-duration', theme.animations.duration);
  root.style.setProperty('--theme-easing', theme.animations.easing);

  // Apply dark mode if needed
  if (config.theme.mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function generateThemeCSS(config: TenantConfig): string {
  const theme = getThemeConfig(config.theme.style || 'elegant');
  
  return `
    :root {
      --primary-color: ${config.primaryColor};
      --secondary-color: ${config.secondaryColor};
      --accent-color: ${config.accentColor};
      
      --theme-background: ${theme.colors.background};
      --theme-surface: ${theme.colors.surface};
      --theme-text: ${theme.colors.text};
      
      --theme-font-family: ${theme.typography.fontFamily};
      --theme-heading-weight: ${theme.typography.headingWeight};
      --theme-body-weight: ${theme.typography.bodyWeight};
      
      --theme-section-spacing: ${theme.spacing.section};
      --theme-container-padding: ${theme.spacing.container};
      
      --theme-radius-sm: ${theme.borderRadius.small};
      --theme-radius-md: ${theme.borderRadius.medium};
      --theme-radius-lg: ${theme.borderRadius.large};
      
      --theme-shadow-sm: ${theme.shadows.small};
      --theme-shadow-md: ${theme.shadows.medium};
      --theme-shadow-lg: ${theme.shadows.large};
      
      --theme-duration: ${theme.animations.duration};
      --theme-easing: ${theme.animations.easing};
    }
    
    body {
      font-family: var(--theme-font-family);
      font-weight: var(--theme-body-weight);
      background-color: var(--theme-background);
      color: var(--theme-text);
    }
    
    h1, h2, h3, h4, h5, h6 {
      font-weight: var(--theme-heading-weight);
    }
    
    .section-spacing {
      padding: var(--theme-section-spacing) 0;
    }
    
    .container-padding {
      padding-left: var(--theme-container-padding);
      padding-right: var(--theme-container-padding);
    }
    
    .theme-transition {
      transition-duration: var(--theme-duration);
      transition-timing-function: var(--theme-easing);
    }
  `;
}